package horcrux.common

import arrow.core.Either
import arrow.core.left
import arrow.core.raise.either
import arrow.core.right
import horcrux.common.api.dtos.HorcruxErrorReason
import horcrux.common.utils.Validatable
import horcrux.common.utils.toOptional
import io.ktor.http.HttpStatusCode
import io.ktor.http.Url
import kotlin.jvm.JvmInline

typealias HorcruxEither<A> = Either<Throwable, A>

interface ValueClass<A, B : ValueClass<A, B>> : Validatable<B>, HasBetterToString {
    val value: A

    override fun betterToString(): String {
        return value.toString()
    }
}

interface ValidatedValueClass<A, B : ValueClass<A, B>> : ValueClass<A, B> {
    override fun validate(): HorcruxEither<B> = either {
        this@ValidatedValueClass as B
    }
}

interface URLValueClass<B : URLValueClass<B>> : ValueClass<String, B> {
    val url: Either<Throwable, Url>
        get() = when (urlRegex.matches(value)) {
            true -> Url(value).right()
            false -> IllegalArgumentException("$value is an invalid URL").left()
        }

    override fun validate(): HorcruxEither<B> = either {
        url.bind()
        this@URLValueClass as B
    }

    companion object {
        val urlRegex: Regex = Regex(
            """(\b(https?|ftp|file)://)?[-A-Za-z0-9+&@#/%?=~_|!:,.;]+[-A-Za-z0-9+&@#/%=~_|]"""
        )
    }
}

interface UniqueValueClass<A, B : UniqueValueClass<A, B>> : ValueClass<A, B> {
    fun confirmUnique(currentValues: Set<B>): HorcruxEither<B> = either {
        if (currentValues.contains(this@UniqueValueClass)) {
            raise(DuplicateValueError(this.toString()))
        }
        this@UniqueValueClass as B
    }

    fun validateAndConfirmUnique(currentValues: Set<B>): HorcruxEither<B> = either {
        validate().bind().confirmUnique(currentValues).bind()
    }

    fun isValidAndUnique(currentValues: Set<B>): Boolean =
        validate().isRight() && confirmUnique(currentValues).isRight()
}

interface HorcruxRequestError {
    val httpStatusCode: HttpStatusCode
    val reasonMaybe: HorcruxErrorReason?

    val shouldLogError: Boolean
        get() = httpStatusCode.value >= 400
}


open class HorcruxRequestSimpleError(
    msgMaybe: String?,
    causeMaybe: Throwable?,
    override val httpStatusCode: HttpStatusCode,
    override val reasonMaybe: HorcruxErrorReason? = null
) : HorcruxRequestError, Throwable(msgMaybe, causeMaybe) {
    constructor(httpStatusCode: HttpStatusCode) : this(null, null, httpStatusCode)
    constructor(httpStatusCode: HttpStatusCode, reasonMaybe: HorcruxErrorReason?) : this(
        null,
        null,
        httpStatusCode,
        reasonMaybe
    )

    constructor(msg: String, httpStatusCode: HttpStatusCode) : this(msg, null, httpStatusCode)
    constructor(cause: Throwable, httpStatusCode: HttpStatusCode) : this(null, cause, httpStatusCode)
}

open class HorcruxRequestCompositeError(
    msgMaybe: String? = null,
    val causes: List<Throwable>,
    override val httpStatusCode: HttpStatusCode,
    override val reasonMaybe: HorcruxErrorReason? = null,
) : HorcruxRequestError, Throwable(msgMaybe)

data class DuplicateValueError(val value: String) : HorcruxRequestSimpleError(HttpStatusCode.Conflict)

@JvmInline
value class GCSBucket(override val value: String) : ValidatedValueClass<String, GCSBucket>

interface HasBetterToString {
    fun betterToString(): String
}
